import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
type InputType = 
  | 'text'
  | 'password'        // 비밀번호
  | 'businessNumber'  // 사업자번호
  | 'residentNumber'  // 주민등록번호
  | 'phone'          // 핸드폰번호
  | 'number'         // 숫자만
  | 'numberWithComma'; // 숫자 + 콤마

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: InputType;
  maxWidth?: number;
  error?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(({ 
  type = 'text',
  value,
  onChange,
  error,
  maxWidth,
  ...props 
}, ref) => {
  const formatValue = (val: string): string => {
    // password 타입일 경우 포맷팅하지 않고 그대로 반환
    if (type === 'password') {
      return val;
    }

    let formatted = val.replace(/[^0-9]/g, ''); // 숫자만 추출

    switch (type) {
      case 'businessNumber':
        if (formatted.length <= 10) {
          formatted = formatted.replace(/(\d{3})(\d{2})?(\d{5})?/, function(_, p1, p2, p3) {
            let parts = [p1];
            if (p2) parts.push(p2);
            if (p3) parts.push(p3);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 12); // xxx-xx-xxxxx

      case 'residentNumber':
        if (formatted.length <= 13) {
          formatted = formatted.replace(/(\d{6})(\d{7})?/, function(_, p1, p2) {
            let parts = [p1];
            if (p2) parts.push(p2);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 14); // xxxxxx-xxxxxxx

      case 'phone':
        if (formatted.length <= 11) {
          formatted = formatted.replace(/(\d{3})(\d{4})?(\d{4})?/, function(_, p1, p2, p3) {
            let parts = [p1];
            if (p2) parts.push(p2);
            if (p3) parts.push(p3);
            return parts.join('-');
          });
        }
        return formatted.slice(0, 13); // xxx-xxxx-xxxx

      case 'numberWithComma':
        return Number(formatted).toLocaleString();

      case 'number':
        return formatted;

      default:
        return val;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = formatValue(e.target.value);
    
    // 원래의 input 이벤트를 시뮬레이트
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(e.target, newValue);
      const event = new Event('input', { bubbles: true });
      e.target.dispatchEvent(event);
    }

    onChange?.(e);
  };

  return (
    <StyledInput
      className='body-regular-16'
      ref={ref}
      value={value}
      type={type === 'password' ? 'password' : 'text'}
      onChange={handleChange}
      $error={error}
      $maxWidth={maxWidth}
      {...props}
    />
  );
});

const StyledInput = styled.input<{ $error?: boolean, $maxWidth?: number }>`
  width: 100%;
  height: 43px;
  max-width: ${props => props.$maxWidth ? `${props.$maxWidth}px` : '100%'};
  padding: 0.5rem;
  border: 1px solid ${props => props.$error ? semanticColors.background.danger.primary : semanticColors.background.primary};
  border-radius: 7px;
  color: ${semanticColors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${semanticColors.background.brand};
    box-shadow: 0 0 0 1px ${semanticColors.background.brand};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }

  &[type="number"] {
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

export { Input }; 